import React from "react";
import {
  type EmbeddedBindingRecord,
  commonBindingsFabric,
} from "@msidentity/SISU/utilities/formatted-text-with-bindings";
import { addQueryStrings, replaceTokens } from "@msidentity/SISU/utilities/strings-helper";
import {
  usernameRecoveryGenericStringsFabric,
  usernameRecoveryResultsViewStringsFabric as strings,
} from "../../../../fabric/username-recovery-strings-fabric";
import UsernameRecoveryConfig from "../../../../username-recovery-config";
import { useUsernameRecoveryContext } from "../../../../username-recovery-context";

/**
 * Embedded Login Link for Username Recovery Flow
 * @param chunks - the chunks of the string passed from react-intl
 * @param usernameVal - username value
 * @param successUrl - success url
 * @returns the link component pointing to the login page with the username value query string
 */
export function LoginLinkWithUsernameVal(
  chunks: string[],
  usernameVal: string,
  successUrl: string,
) {
  const queryStringParams = new Map<string, string>();
  queryStringParams.set("username", encodeURIComponent(usernameVal));

  return <a href={addQueryStrings(successUrl, queryStringParams)}>{chunks}</a>;
}

type ResultsDescription = {
  description: string;
  embeddedBindings?: EmbeddedBindingRecord;
};

export type RecoveryResultsStringsFabric = {
  title: string;
  description?: ResultsDescription;
  heading: string;
  primaryButtonText: string;
};

export const useRecoveryResultsStringsFabric = (noAssociatedAccounts: boolean) => {
  const { successUrl } = UsernameRecoveryConfig.instance;
  const {
    viewState: {
      usernameVal,
      usernameDisplay,
      proofState: { isSignInAllowedProof },
    },
  } = useUsernameRecoveryContext();

  const heading = isSignInAllowedProof
    ? replaceTokens(strings.proofIsAnAliasDescription, usernameDisplay)
    : replaceTokens(strings.proofIsNotAnAliasDescription, usernameDisplay);
  const { title } = usernameRecoveryGenericStringsFabric;
  let primaryButtonText = usernameRecoveryGenericStringsFabric.signin;
  let description: ResultsDescription | undefined;
  if (noAssociatedAccounts) {
    description = { description: strings.noAssociatedAccountsDescription };
    primaryButtonText = usernameRecoveryGenericStringsFabric.tryAgain;
  } else if (isSignInAllowedProof) {
    const embeddedBindingsLoginLink = {
      loginLink: (chunks: string[]) => LoginLinkWithUsernameVal(chunks, usernameVal, successUrl),
      boldText: commonBindingsFabric.boldText,
    };
    description = {
      description: replaceTokens(strings.existingMSAAccountDescription, usernameDisplay),
      embeddedBindings: embeddedBindingsLoginLink,
    };
  }

  return {
    title,
    description,
    heading,
    primaryButtonText,
  };
};
